import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import type { DiagnosticTabProps } from "@/components/projects/diagnostic/DiagnosticSection";
import { processDestination } from "@/components/projects/diagnostic/diagnosticData";
import { FilterButton } from "@/components/projects/diagnostic/orgStructure/FilterButton";
import { NO_VALUE } from "@/components/projects/diagnostic/sectionState";
import { useDiagnosticInScopeItems } from "@/hooks/useDiagnosticScopeItems";
import { formatCount } from "@/lib/format";
import { cn } from "@/lib/utils";

import { PROCESS_DESTINATIONS, type ProcessDestinationKey } from "./processFitLabels";
import { scopeItemsBasis } from "./scopeItemSource";

const CARD = "overflow-hidden rounded-lg border border-[#e3e8ef] bg-white";
const HEAD = "px-3 py-2 font-mono text-[9px] font-medium uppercase tracking-[0.08em] text-[#7b8799]";
const CELL = "px-3 py-2 align-top text-[12px] text-[#08254c]";

function destinationFromSearch(value: string | null): ProcessDestinationKey | null {
  return value === "activate" || value === "decision" ? value : null;
}

/**
 * "Destino provável dos processos", o detalhe do card do Ato 2.
 *
 * Pedido do David em 19/10/2026: os scope items do escopo por destino, com os
 * nomes da legenda do card ("Padrão GROW direto" e "Decisão assistida"). O
 * destino clicado no card chega filtrado, e o filtro fica na URL para o "Voltar"
 * e o link direto chegarem no mesmo recorte.
 */
export function ProcessDestinationDetail({ sections, validation }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const [search, setSearch] = useSearchParams();
  const selected = destinationFromSearch(search.get("destination"));
  const destinations = PROCESS_DESTINATIONS(t);
  const total = processDestination(sections).total;
  const items = useDiagnosticInScopeItems(projectId, scopeItemsBasis(validation));

  const choose = (key: ProcessDestinationKey | null) => {
    const next = new URLSearchParams(search);
    if (key) next.set("destination", key);
    else next.delete("destination");
    setSearch(next, { replace: true });
  };

  const all = items.data ?? [];
  const shown = selected ? all.filter((item) => item.destination === selected) : all;

  return (
    <div className="space-y-5">
      <p className="max-w-3xl text-[13px] leading-relaxed text-[#596b85]">
        {total != null
          ? t(
              "Preliminary classification, subject to your team's validation · base: {{total}} scope items",
              { total: formatCount(total) },
            )
          : t("Preliminary classification, subject to your team's validation")}
      </p>

      {items.isLoading ? (
        <p className="text-[13px] text-[#596b85]">{t("Loading…")}</p>
      ) : items.isError ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#f3c7c2] bg-[#fdf3f2] px-4 py-3 text-[13px] text-[#a23b2f]">
          {t("Could not load the scope items.")}
          <button
            type="button"
            onClick={() => void items.refetch()}
            className="rounded-md border border-current px-3 py-1 text-[12px] font-semibold"
          >
            {t("Try again")}
          </button>
        </div>
      ) : (
        <div data-testid="process-destination-items" className={CARD}>
          <div className="flex flex-wrap gap-2 border-b border-[#eef1f5] px-4 py-3">
            <FilterButton active={selected === null} onClick={() => choose(null)}>
              {t("All")} {formatCount(all.length)}
            </FilterButton>
            {destinations.map((destination) => (
              <FilterButton
                key={destination.key}
                active={selected === destination.key}
                onClick={() => choose(destination.key)}
              >
                {destination.label}{" "}
                {formatCount(all.filter((item) => item.destination === destination.key).length)}
              </FilterButton>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse">
              <thead>
                <tr className="border-b border-[#e8ebf0] bg-[#f7f9fc] text-left">
                  <th className={HEAD}>{t("Scope item")}</th>
                  <th className={HEAD}>{t("Name")}</th>
                  <th className={HEAD}>{t("LoB")}</th>
                  <th className={HEAD}>{t("Destination")}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((item) => {
                  const destination = destinations.find((entry) => entry.key === item.destination);
                  return (
                    <tr
                      key={`${item.destination}-${item.scope_item_id}`}
                      data-testid={`process-destination-item-${item.scope_item_id}`}
                      className="border-b border-[#eef1f5] last:border-b-0"
                    >
                      <td className={cn(CELL, "font-mono")}>{item.scope_item_id}</td>
                      <td className={CELL}>{item.scope_item_name ?? NO_VALUE}</td>
                      <td className={CELL}>{item.lob ?? NO_VALUE}</td>
                      <td className={CELL}>
                        <span className="inline-flex items-center gap-1.5">
                          <span aria-hidden className={cn("h-2 w-2 rounded-sm", destination?.className)} />
                          {destination?.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
